import Keycloak from "keycloak-js";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { setKeycloakInstance } from "@/api/client";

const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL || "http://localhost:8080",
  realm: import.meta.env.VITE_KEYCLOAK_REALM || "alphaprox",
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "alphaprox-frontend",
});

interface KeycloakContextValue {
  keycloak: Keycloak;
  initialized: boolean;
}

const KeycloakContext = createContext<KeycloakContextValue>({
  keycloak,
  initialized: false,
});

export function useKeycloakContext() {
  return useContext(KeycloakContext);
}

interface KeycloakProviderProps {
  children: ReactNode;
}

// Build-time flag (same one the ProtectedRoute reads). When true the
// provider skips the whole OIDC handshake and renders children with a
// never-authenticated Keycloak stub — ProtectedRoute then ignores the
// auth state because the flag is set.
const E2E_BYPASS_AUTH = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

export function KeycloakProvider({ children }: KeycloakProviderProps) {
  const [initialized, setInitialized] = useState(E2E_BYPASS_AUTH);

  useEffect(() => {
    if (E2E_BYPASS_AUTH) {
      // No OIDC network calls, no redirects — drop straight into the
      // app. Callers must not rely on ``keycloak.authenticated`` being
      // true in this mode; the bypass is in ProtectedRoute.
      return;
    }
    keycloak
      .init({
        onLoad: "check-sso",
        silentCheckSsoRedirectUri:
          window.location.origin + "/silent-check-sso.html",
        pkceMethod: "S256",
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        if (authenticated) {
          setKeycloakInstance(keycloak);
        } else {
          keycloak.login();
          return;
        }
        setInitialized(true);
      })
      .catch((err) => {
        console.error("Keycloak init failed:", err);
        setInitialized(true);
      });

    keycloak.onAuthRefreshSuccess = () => {
      setKeycloakInstance(keycloak);
    };

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => {
        keycloak.login();
      });
    };
  }, []);

  if (!initialized) {
    // Show a blank dark screen instead of a spinner to avoid flicker
    // during the fast OIDC callback round-trip.
    return <div className="h-screen w-screen bg-gray-950" />;
  }

  return (
    <KeycloakContext.Provider value={{ keycloak, initialized }}>
      {children}
    </KeycloakContext.Provider>
  );
}
